// src/oura/components/site/siteCopy.ts
//
// The words and small rules the Site screen's cards share: the verdict band
// for a 0 to 100 score, why a score is absent, the five readability checks in
// plain words and the audit check behind each, and the fallback that splits a
// backend strength or weakness into a title and a line.

import type { AeoCrawlAbsence, AeoReadabilityAbsence, AeoReadabilityComponent } from "@service/aeo";
import type { Tone } from "@oura/ui";
import { dateAfter } from "../../lib/oura";
import { ROLE_LABEL, type PageRole } from "../../pageWorkshop";

/**
 * The verdict word for a Site Health or AI Readability score, on the
 * backend's own bands (`_band` and `readability_band`: 75, 50, 25). The
 * backend's verdict is a whole sentence; a tile has room for its first word,
 * and deriving it from the same thresholds keeps the two in step.
 */
export function scoreBand(score: number): { word: string; text: string } {
    if (score >= 75) return { word: "Strong", text: "text-emerald-700" };
    if (score >= 50) return { word: "Developing", text: "text-amber-700" };
    if (score >= 25) return { word: "Weak", text: "text-rose-700" };
    return { word: "Critical", text: "text-rose-700" };
}

/** The tag tone for a Site Health or AI Readability verdict, on the same bands as `scoreBand`. */
export function bandTone(score: number): Tone {
    return score >= 75 ? "green" : score >= 50 ? "amber" : "rose";
}

/** The three bands a share of pages falls in. */
export type ShareTone = Extract<Tone, "green" | "amber" | "rose">;

/** The dot for a share of pages covered: the prototype's 70 / 50 bands. */
export function shareTone(covered: number, applicable: number): ShareTone {
    const pct = applicable > 0 ? (covered / applicable) * 100 : 0;
    return pct >= 70 ? "green" : pct >= 50 ? "amber" : "rose";
}

/** The text colour for a share of pages covered, the same bands as its dot. */
export const SHARE_TEXT: Record<ShareTone, string> = {
    green: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
};

/**
 * What Site Health asks, the line the live Overview card carried under its
 * title. "Three places" named: Google search, Google's AI and the AI
 * assistants, the places `places.ts` reads.
 */
export const SITE_HEALTH_LINE = "Can Google reach and read every page. Feeds all three places: Google search, Google's AI and the AI assistants.";

/** Why Site Health has no number, in the client's words. A named state, never
 *  a 0: the same contract `siteHealthScoreAbsent` carries on the wire. */
export const SITE_HEALTH_ABSENT_TEXT: Record<AeoCrawlAbsence, string> = {
    crawl_failed: "The crawler could not finish reading this site on this scan, so there is no site-health score.",
    crawl_blocked: "Every page the crawler fetched came back unreadable, usually a firewall or a rate limit, so there is no site-health score.",
    not_crawled: "The crawl ran but produced nothing to score, so there is no site-health score.",
};

/**
 * Why there is no AI Readability score (#3765). Four named backend states,
 * plus the whole group missing, which is a scan finalized before
 * ourteam-backend#4877. None of them is "0".
 */
export const READABILITY_ABSENT_TEXT: Record<AeoReadabilityAbsence, string> = {
    crawl_did_not_run: "No pages were read on this scan, so there is nothing to score yet.",
    crawl_blocked: "Too few pages could be read on this scan to score this honestly.",
    // The important one: a derelict-looking score on a perfectly good site.
    shell_unrendered: "This site builds its content in the browser, and the scan read the page before that happened. That is an unread site, not a low score.",
    no_components_yet: "The checks this score is made of have not shipped yet. It fills in as they land.",
};

/**
 * The five checks AI Readability is the mean of, in the backend's order
 * (AI_READABILITY_COMPONENTS in aeo_audit.py), each with the audit checks
 * that fail it. A component's "How to fix" opens the first of those checks
 * that found something, so the drawer is always the real finding.
 *
 * Labels are generic across clinics, beauty and fitness: the backend counts
 * prices over treatment and service pages and staff over team pages.
 */
export const READABILITY_ROWS: Array<{
    key: AeoReadabilityComponent["key"];
    label: string;
    line: string;
    checks: string[];
}> = [
    { key: "headings", label: "Pages with a heading", line: "A clear title a machine can quote", checks: ["missing_h1"] },
    // `no_schema` counts beside `schema_no_entity` on the backend: a page fails
    // by carrying no markup, or only page furniture.
    { key: "entity_markup", label: "Pages with entity markup", line: "Says in code what the business is", checks: ["schema_no_entity", "no_schema"] },
    { key: "prices", label: "Service pages with a price", line: "AI quotes the rival that shows one", checks: ["no_price_signal"] },
    { key: "staff", label: "Staff pages linked", line: "Who works here, tied to this business", checks: ["staff_unattributed"] },
    { key: "branches", label: "Branch records found", line: "The address AI matches to the map", checks: ["branch_records_missing"] },
];

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** Fix first: the weakest component with the most pages behind it. */
export function fixFirst(components: AeoReadabilityComponent[]): AeoReadabilityComponent | undefined {
    return components
        .filter((c) => c.covered < c.applicable)
        .sort((a, b) => a.score - b.score || (b.applicable - b.covered) - (a.applicable - a.covered))[0];
}

/** The fix for a component, as an action: "Add prices to 8 service pages". */
export function fixFirstAction(c: AeoReadabilityComponent): string {
    const n = c.applicable - c.covered;
    switch (c.key) {
        case "prices": return `Add prices to ${n} service ${plural(n, "page", "pages")}`;
        case "staff": return `Link ${n} staff ${plural(n, "page", "pages")} to the business`;
        case "entity_markup": return `Add entity markup to ${n} ${plural(n, "page", "pages")}`;
        case "headings": return `Give ${n} ${plural(n, "page", "pages")} a heading`;
        case "branches": return `Add ${n} missing branch ${plural(n, "record", "records")}`;
    }
}

/** What is missing on a component, as a phrase: "No prices on 8 service pages". */
export function dragPhrase(c: AeoReadabilityComponent): string {
    const n = c.applicable - c.covered;
    switch (c.key) {
        case "prices": return `No prices on ${n} service ${plural(n, "page", "pages")}`;
        case "staff": return `${n} staff ${plural(n, "page", "pages")} not linked to the business`;
        case "entity_markup": return `No entity markup on ${n} ${plural(n, "page", "pages")}`;
        case "headings": return `No heading on ${n} ${plural(n, "page", "pages")}`;
        case "branches": return `${n} branch ${plural(n, "record", "records")} missing`;
    }
}

/**
 * The client's words for the same two lines: no "service", "staff" or
 * "entity markup" pages, which a restaurant owner read as somebody else's
 * business (usability test, 14 Oct 2026). The counts are the same.
 */
export function clientFixAction(c: AeoReadabilityComponent): string {
    const n = c.applicable - c.covered;
    const pages = plural(n, "page", "pages");
    switch (c.key) {
        case "prices": return `Add prices to ${n} ${pages}`;
        case "staff": return `Link ${n} team ${pages} to your business`;
        case "entity_markup": return `Name your business in code on ${n} ${pages}`;
        case "headings": return `Give ${n} ${pages} a heading`;
        case "branches": return `Add ${n} missing branch ${plural(n, "record", "records")}`;
    }
}

export function clientDragPhrase(c: AeoReadabilityComponent): string {
    const n = c.applicable - c.covered;
    const pages = plural(n, "page", "pages");
    switch (c.key) {
        case "prices": return `No prices on ${n} ${pages}`;
        case "staff": return `${n} team ${pages} not linked to your business`;
        case "entity_markup": return `${n} ${pages} don't name your business`;
        case "headings": return `No heading on ${n} ${pages}`;
        case "branches": return `${n} branch ${plural(n, "record", "records")} missing`;
    }
}

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * A backend strength or weakness as a title and a line.
 *
 * The wire carries one string per item (BE handoff "Structured strengths and
 * weaknesses" adds a title, a line and the check key). Strengths join the two
 * halves with a spaced dash; the dash is dropped and the halves split there.
 * A string with no dash is a title with no line.
 *
 * A weakness for a check the backend has no copy for arrives as
 * "12 \u00d7 pages missing an h1" (`_weaknesses` in aeo_overview.py); it reads
 * "12 pages missing an h1" here until the structured items ship.
 */
export function splitItem(text: string): { title: string; line: string | null } {
    const counted = /^(\d+) \u00d7 (.*)$/.exec(text.trim());
    const plain = counted ? `${counted[1]} ${counted[2]}` : text;
    const m = /\s[\u2013\u2014]\s/.exec(plain);
    if (!m) return { title: capitalise(plain.trim()), line: null };
    const line = plain.slice(m.index + m[0].length).trim();
    return {
        title: capitalise(plain.slice(0, m.index).trim()),
        line: line ? capitalise(line) : null,
    };
}

/** "16 Sep" for a crawl or scan date, or null when there is no usable date. */
export function dayMonth(iso: string | null | undefined): string | null {
    if (!iso || Number.isNaN(new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso).getTime())) return null;
    return dateAfter(iso, 0);
}

/** A page role in sentence case: the workshop's labels are title case. */
export function roleWord(role: PageRole): string {
    const label = ROLE_LABEL[role];
    return label.charAt(0) + label.slice(1).toLowerCase();
}

/**
 * Client-readable names for the audit checks that matter most: the PDF
 * report's page 5 and the client Website page both read these, so a
 * finding is called the same thing in both places.
 */
export const CLIENT_ISSUE_COPY: Record<string, { label: string; why: string }> = {
    no_schema: {
        label: "Pages missing structured data",
        why: "No labels telling AI what your business is, where it is, or what it offers.",
    },
    thin_content: {
        label: "Thin content pages",
        why: "Too little text, so AI has almost nothing worth quoting.",
    },
    missing_meta: {
        label: "Pages without meta descriptions",
        why: "Search and AI previews fall back to arbitrary page text.",
    },
    missing_alt: {
        label: "Images missing alt text",
        why: "Accessibility and image-search signals left unused.",
    },
    broken_4xx: {
        label: "Pages returning an error",
        why: "Dead ends that waste crawl budget and lose visitors.",
    },
    orphan_pages: {
        label: "Pages nothing links to",
        why: "Crawlers and visitors have no route to them.",
    },
    broken_outbound: {
        label: "Pages linking to articles that are gone",
        why: "A link to a story that has been taken down sends readers nowhere and reads as neglect.",
    },
};

/**
 * Every check the client's Website page can list, in plain words: the problem,
 * then what it costs them. A restaurant owner reads these between services,
 * so no crawl, schema, canonical or status-code words. The PDF report keeps
 * its own wording (CLIENT_ISSUE_COPY); a check missing here falls back to it,
 * then to the check's own label.
 */
export const CLIENT_SITE_ISSUE_COPY: Record<string, { label: string; why: string }> = {
    broken_4xx: { label: "Pages that show an error", why: "Visitors and AI hit a dead end." },
    orphan_pages: { label: "Pages nothing links to", why: "Visitors and AI cannot find them." },
    no_schema: { label: "Pages that don't tell AI what you are", why: "No hidden labels for your business, address or what you offer." },
    schema_no_entity: { label: "Pages that don't name your business in code", why: "AI cannot tell whose page it is." },
    robots_missing: { label: "No robots.txt file", why: "Search engines are never told where your page list is." },
    missing_meta: { label: "Pages with no search preview text", why: "Google and AI show a random line instead." },
    dup_titles: { label: "Pages with a missing or repeated title", why: "Google cannot tell them apart." },
    redirect_chains: { label: "Links that bounce through several addresses", why: "Each hop slows the page down." },
    canonicals: { label: "Pages without a main address set", why: "One page can show up under several addresses." },
    thin_content: { label: "Pages with too few words", why: "AI has almost nothing to quote." },
    missing_alt: { label: "Images with no description", why: "Google and AI cannot tell what they show." },
    cwv: { label: "Slow-loading pages", why: "Visitors on phones wait too long." },
    broken_outbound: { label: "Links to pages that are gone", why: "They send visitors nowhere." },
    no_price_signal: { label: "Pages with no prices", why: "AI quotes the rival that shows one." },
    missing_h1: { label: "Pages with no heading", why: "AI has no clear title to quote." },
};

/**
 * The five readability checks in the client's words: the same keys and
 * order as READABILITY_ROWS, which keeps the staff wording.
 */
export const CLIENT_READABILITY_ROWS: Record<AeoReadabilityComponent["key"], { label: string; line: string }> = {
    headings: { label: "Pages with a heading", line: "A clear title AI can quote" },
    entity_markup: { label: "Pages that name your business in code", line: "Tells AI what you are and where" },
    prices: { label: "Pages with prices", line: "AI quotes the rival that shows one" },
    staff: { label: "Team pages linked", line: "Who works here, tied to your business" },
    branches: { label: "Branch records found", line: "The address AI matches to the map" },
};
